import ringMask from "@/assets/tokuro-mark-ring.png";
import pyramid from "@/assets/tokuro-mark-pyramid.png";
import { cn } from "@/lib/utils";

/**
 * Official Tokuro.ai brand mark + wordmark lockup.
 * Composed programmatically so the mark colours theme cleanly for
 * both dark (sign-in brand panel) and light (in-app header) surfaces.
 *
 * Brand palette (official):
 *   Navy   #0A1230
 *   Orange #E38A2E
 *   Grey   #9A948A
 *   Cream  #F5F4F1
 *
 * The mark's ring is delivered as a black-on-transparent PNG used as a
 * CSS `mask-image`; the ring colour is driven by the parent tone. The
 * orange pyramid is an <img> overlay that stays orange in every variant.
 */

const NAVY = "#0A1230";
const WHITE = "#FFFFFF";

// Aspect ratio of the mark PNG (width / height). Tight-cropped so no padding.
const MARK_ASPECT = 119 / 129;

// Wordmark font-size only — mark size is derived dynamically to span both
// wordmark + tagline when the tagline is present, so the mark is BIG ENOUGH
// to visually span the full lockup.
const WORDMARK_PX = {
  sm: 16,
  md: 22,
  lg: 32,
  xl: 42,
};

const TAG_PX = {
  sm: 8,
  md: 9,
  lg: 10,
  xl: 11,
};

// Gap between the wordmark line and the tagline line
const STACK_GAP = 6;

export function TokuroMark({ heightPx = 32, variant = "dark", className }) {
  const ringColor = variant === "light" ? WHITE : NAVY;
  const width = heightPx * MARK_ASPECT;
  return (
    <span
      className={cn("relative inline-block flex-none", className)}
      style={{ width, height: heightPx }}
      aria-hidden="true"
      data-testid="tokuro-mark"
    >
      {/* Ring: PNG used as mask, background-color drives the tint */}
      <span
        className="absolute inset-0"
        style={{
          backgroundColor: ringColor,
          WebkitMaskImage: `url(${ringMask})`,
          maskImage: `url(${ringMask})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
      />
      {/* Orange pyramid overlay (stays orange in every variant) */}
      <img
        src={pyramid}
        alt=""
        className="absolute inset-0 h-full w-full object-contain"
        draggable={false}
      />
    </span>
  );
}

/**
 * Full brand lockup: mark + "Tokuro.ai" wordmark + optional
 * "A DAAKYI Platform" tagline underneath.
 *
 * Props:
 *   variant   "dark" (default) → navy text for light backgrounds
 *             "light"           → white text for dark backgrounds
 *   size      "sm" | "md" | "lg" | "xl" (default "md")
 *   showTagline  render the "A DAAKYI Platform" endorsement (default true for lg/xl)
 *   className    extra classes on the outer wrapper
 */
export function TokuroLogo({
  variant = "dark",
  size = "md",
  showTagline,
  className,
  testId = "tokuro-logo",
}) {
  const light = variant === "light";
  const wordColor = light ? "text-white" : "text-[#0A1230]";
  const tagColor = light ? "text-white/55" : "text-[#0A1230]/50";
  const orangeDot = "text-[#E38A2E]";
  const withTag = showTagline ?? (size === "lg" || size === "xl");

  // Mark height = wordmark line height + (tagline line height + gap) when a
  // tagline is present. This makes the icon visually span the full lockup
  // instead of only matching the wordmark line.
  const wordPx = WORDMARK_PX[size] ?? WORDMARK_PX.md;
  const tagPx = TAG_PX[size] ?? TAG_PX.md;
  const markHeight = withTag ? wordPx + STACK_GAP + tagPx : wordPx;

  return (
    <span
      className={cn("inline-flex items-center gap-2", className)}
      data-testid={testId}
      role="img"
      aria-label="Tokuro.ai — A DAAKYI Platform"
    >
      <TokuroMark heightPx={markHeight} variant={variant} />
      <span className="flex flex-col leading-none">
        <span
          className={cn(
            "font-heading font-semibold tracking-tight",
            wordColor,
          )}
          style={{ fontSize: `${wordPx}px`, letterSpacing: "-0.02em" }}
        >
          Tokuro<span className={orangeDot}>.</span>ai
        </span>
        {withTag && (
          <span
            className={cn(
              "font-mono uppercase tracking-[0.24em]",
              tagColor,
            )}
            style={{ fontSize: `${tagPx}px`, marginTop: `${STACK_GAP}px` }}
          >
            A DAAKYI Platform
          </span>
        )}
      </span>
    </span>
  );
}

export default TokuroLogo;
